import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Calculator, Save, Shield, TrendingUp, AlertCircle, FileText, ChevronRight, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const UnderwritingCalculator = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const property = location.state?.property;

  const [mode, setMode] = useState(property?.bot_type || 'land');
  const [assumptions, setInputs] = useState({
    // Standard Assumptions
    purchasePrice: property?.price || 0,
    closingCosts: 3000,
    holdingCosts: 1500,
    
    // Land-Specific
    marketValue: property?.price * 1.5 || 0,
    assignmentFee: 5000,
    ddCosts: 1000,
    
    // Section 8
    bedrooms: property?.bedrooms || 3,
    fmr: 2100,
    opexRatio: 40,
    downPayment: 5000,
    
    // SFH/Wholesale
    arv: property?.price * 1.4 || 0,
    repairs: 25000,
  });

  const updateInput = (key, val) => setInputs(prev => ({ ...prev, [key]: parseFloat(val) || 0 }));

  const calculateResults = () => {
    if (mode === 'land') {
      const offerPrice = assumptions.purchasePrice;
      const profit = assumptions.assignmentFee;
      const margin = (profit / assumptions.marketValue) * 100;
      const capPct = (offerPrice / assumptions.marketValue) * 100;
      
      return {
        title: "Land Assignment Analysis",
        metrics: [
          { label: "Purchase % of Market", value: `${capPct.toFixed(1)}%`, status: capPct <= 65 ? 'good' : 'warning' },
          { label: "Projected Assignment Fee", value: `$${profit.toLocaleString()}`, status: 'good' },
          { label: "Total All-In Cost", value: `$${(offerPrice + assumptions.closingCosts + assumptions.ddCosts).toLocaleString()}` }
        ],
        recommendation: capPct <= 40 ? "STRONG BUY - High Builder Demand" : "MODERATE - Verify Road Access"
      };
    }
    
    if (mode === 'section8') {
      const noi = (assumptions.fmr * 12) * (1 - (assumptions.opexRatio / 100));
      const cashFlow = (noi / 12) - (500); // Mock debt
      return {
        title: "Section 8 Rental Model",
        metrics: [
          { label: "Monthly Net Cash Flow", value: `$${cashFlow.toFixed(0)}/mo`, status: cashFlow > 300 ? 'good' : 'warning' },
          { label: "OpEx Ratio", value: `${assumptions.opexRatio}%` },
          { label: "Projected Annual NOI", value: `$${noi.toLocaleString()}` }
        ],
        recommendation: cashFlow > 500 ? "EXCELLENT - Exceeds Tom Cruz Threshold" : "MARGINAL - Reduce OPEX"
      };
    }

    return null;
  };

  const results = calculateResults();

  return (
    <div className="max-w-6xl mx-auto p-8 space-y-10 page-transition">
      <header className="flex justify-between items-end border-b pb-8 border-slate-200">
        <div className="space-y-1">
          <Badge variant="outline" className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Phase 3: Underwriting</Badge>
          <h1 className="text-3xl font-black tracking-tighter uppercase">Professional Underwriter</h1>
          <p className="text-sm text-slate-500 max-w-md">Verify assumptions, stress-test margins, and generate investment recommendations.</p>
        </div>
        <div className="flex gap-3">
           <Button variant="outline" className="h-12 px-6 font-bold border-2"><FileText size={18} className="mr-2"/> Export Memo</Button>
           <Button onClick={() => toast.success("Analysis Saved to Operational Center")} className="h-12 px-8 font-black uppercase tracking-wider bg-slate-900"><Save size={18} className="mr-2"/> Save Analysis</Button>
        </div>
      </header>

      <div className="grid grid-cols-12 gap-10">
        {/* Input Column */}
        <div className="col-span-12 lg:col-span-7 space-y-8">
          <Card className="border-2 border-slate-100 shadow-sm">
            <CardHeader className="bg-slate-50/50 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <Shield className="text-slate-400" size={20} />
                <CardTitle className="text-lg font-bold">Deal Parameters</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <Tabs value={mode} onValueChange={setMode} className="w-full">
                <TabsList className="grid grid-cols-4 h-12 bg-slate-100 p-1 mb-8">
                  <TabsTrigger value="land" className="text-xs font-black uppercase italic">Land</TabsTrigger>
                  <TabsTrigger value="section8" className="text-xs font-black uppercase italic">Section 8</TabsTrigger>
                  <TabsTrigger value="wholesale" className="text-xs font-black uppercase italic">Wholesale</TabsTrigger>
                  <TabsTrigger value="multifamily" className="text-xs font-black uppercase italic">Multifamily</TabsTrigger>
                </TabsList>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Target Purchase Price</Label>
                    <Input type="number" value={assumptions.purchasePrice} onChange={e => updateInput('purchasePrice', e.target.value)} className="h-12 font-bold text-lg border-2 border-slate-100 focus:border-slate-900 transition-all" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Closing Costs</Label>
                    <Input type="number" value={assumptions.closingCosts} onChange={e => updateInput('closingCosts', e.target.value)} className="h-12 font-bold border-slate-100" />
                  </div>

                  {mode === 'land' && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Market Value (Comps)</Label>
                        <Input type="number" value={assumptions.marketValue} onChange={e => updateInput('marketValue', e.target.value)} className="h-12 font-bold border-slate-100" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Target Assignment Fee</Label>
                        <Input type="number" value={assumptions.assignmentFee} onChange={e => updateInput('assignmentFee', e.target.value)} className="h-12 font-bold border-slate-100" />
                      </div>
                    </>
                  )}

                  {mode === 'section8' && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">HUD FMR Rent</Label>
                        <Input type="number" value={assumptions.fmr} onChange={e => updateInput('fmr', e.target.value)} className="h-12 font-bold border-slate-100" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">OPEX Ratio (%)</Label>
                        <Input type="number" value={assumptions.opexRatio} onChange={e => updateInput('opexRatio', e.target.value)} className="h-12 font-bold border-slate-100" />
                      </div>
                    </>
                  )}
                </div>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Output Column */}
        <div className="col-span-12 lg:col-span-5 space-y-6">
          <Card className="bg-slate-900 text-white shadow-2xl border-none overflow-hidden">
            <CardHeader className="border-b border-white/10 p-8">
              <div className="flex items-center justify-between">
                <div className="p-3 rounded-2xl bg-white/10">
                  <TrendingUp size={24} />
                </div>
                <Badge className="bg-emerald-500 text-white font-black uppercase italic text-[10px]">High Yield</Badge>
              </div>
              <CardTitle className="text-2xl font-black uppercase italic pt-4">{results?.title}</CardTitle>
            </CardHeader>
            <CardContent className="p-8 space-y-8">
              <div className="space-y-6">
                {results?.metrics.map((m, i) => (
                  <div key={i} className="flex justify-between items-end border-b border-white/10 pb-4 last:border-0">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{m.label}</span>
                    <span className={`text-2xl font-black font-mono ${m.status === 'good' ? 'text-emerald-400' : 'text-white'}`}>{m.value}</span>
                  </div>
                ))}
              </div>

              <div className="p-6 rounded-2xl bg-white/5 border border-white/10 space-y-3">
                 <div className="flex items-center gap-2 text-slate-400">
                    <AlertCircle size={14} />
                    <span className="text-[10px] font-black uppercase tracking-widest">Bot Recommendation</span>
                 </div>
                 <p className="text-sm font-bold italic">"{results?.recommendation}"</p>
              </div>

              <Button 
                onClick={() => navigate('/legion', { state: { property, mode } })}
                className="w-full h-16 bg-white text-slate-900 hover:bg-slate-100 font-black uppercase italic text-lg shadow-xl shadow-white/5"
              >
                Launch Execution Workflow <ChevronRight className="ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UnderwritingCalculator;
